import { toast } from 'react-hot-toast';

/**
 * Standard error handling function for API requests
 * @param error The error object
 * @param customMessage Optional custom message to display
 * @param silent Whether to suppress toast notifications
 */
export const handleError = (error: unknown, customMessage?: string, silent = false): void => {
  const errorMessage = customMessage || getErrorMessage(error);
  
  // Log all errors to console
  console.error(errorMessage, error);
  
  // Display toast notification unless silent mode is enabled
  if (!silent) {
    toast.error(errorMessage);
  }
};

/**
 * Extract a user-friendly error message from various error types
 */
export const getErrorMessage = (error: unknown): string => {
  if (typeof error === 'string') {
    return error;
  }
  
  if (error instanceof Error) {
    return error.message;
  }
  
  if (typeof error === 'object' && error !== null) {
    if ('message' in error && typeof (error as any).message === 'string') {
      return (error as any).message;
    }
    
    if ('error' in error && typeof (error as any).error === 'string') {
      return (error as any).error;
    }
  }
  
  return 'An unknown error occurred';
};

/**
 * Wrap async functions with standard error handling
 */
export const withErrorHandling = <T>(
  asyncFn: () => Promise<T>,
  errorMessage = 'An error occurred',
  silent = false
): Promise<T> => {
  return asyncFn().catch((error) => {
    handleError(error, errorMessage, silent);
    throw error;
  });
}; 